import { LockKey } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { IdeGrant } from "~/api/ideTypes";
import {
  useCurrentWorkspace,
  useIdeGrantActivity,
  useIdeGrants,
  useRevokeIdeGrant,
  useUpdateWorkspacePreferences,
  useViewer,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Pill } from "~/features/automation/components";
import { GrantReviewRow } from "~/features/console/GrantReviewRow";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

/** `IdeAccessGrant.DeclineCooldownHours` — the same 24 h the server enforces. */
const DECLINE_COOLDOWN_HOURS = 24;

/**
 * "All of the owner's machines" is what an unscoped grant IS, and saying it plainly matters:
 * a grant rendered without its scope reads far narrower than it is.
 */
function scopeLabel(
  grant: IdeGrant,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  const parts: string[] = [];
  if (grant.scope.workspacePathPrefix) {
    parts.push(grant.scope.workspacePathPrefix);
  }
  if (grant.scope.machineId) {
    parts.push(t("machine {{id}}…", { id: grant.scope.machineId.slice(0, 8) }));
  }
  return parts.length === 0 ? t("All of the owner's machines") : parts.join(" · ");
}

function GrantActivity({ grantId }: { grantId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useIdeGrantActivity(grantId);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  // §E-8.7 — the contract is live and the trail is empty BY CONSTRUCTION until
  // `AiAction.GrantId` ships. Rendering "no activity" would state a fact nobody measured.
  if (!data || data.length === 0) {
    return (
      <p className="text-small text-text-secondary">
        {t("Per-use records land when the audit column ships.")}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1">
      {data.map((entry, index) => (
        <li key={JSON.stringify(entry)} className="font-mono text-small text-text-secondary">
          {`${index + 1}. ${JSON.stringify(entry)}`}
        </li>
      ))}
    </ul>
  );
}

function GrantRow({
  grant,
  viewerId,
  canAdminister,
  asOwner,
}: {
  grant: IdeGrant;
  viewerId: string | undefined;
  canAdminister: boolean;
  asOwner: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const revoke = useRevokeIdeGrant();

  const isOwner = grant.ownerUserId === viewerId;
  const isGrantee = grant.granteeUserId === viewerId;
  const canRevoke = isOwner || isGrantee || canAdminister;

  const cooldownUntil =
    grant.status === "declined" && grant.declinedAt
      ? Date.parse(grant.declinedAt) + DECLINE_COOLDOWN_HOURS * 3_600_000
      : null;

  return (
    <li className="flex flex-col gap-1.5 border-b border-divider px-3 py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body text-text">
          {asOwner
            ? (grant.granteeUserName ?? grant.granteeUserId)
            : (grant.ownerUserName ?? grant.ownerUserId)}
        </span>
        <Pill
          tone={
            grant.status === "active"
              ? "success"
              : grant.status === "pending"
                ? "warning"
                : "neutral"
          }
        >
          {grant.status}
        </Pill>
        {/* Never "access granted" without its level, scope and expiry on the same line. */}
        <Pill>{grant.level ?? "—"}</Pill>
        <span className="text-small text-text-secondary">{scopeLabel(grant, t)}</span>
        {grant.expiresAt ? (
          <span className="text-small text-text-tertiary">
            {t("expires {{when}}", { when: formatRelativeTime(grant.expiresAt, i18n.language) })}
          </span>
        ) : null}
        {grant.alwaysAllow ? <Pill tone="warning">{t("Always allowed")}</Pill> : null}
      </div>

      <p className="line-clamp-2 text-small text-text-secondary" title={grant.purpose}>
        {grant.purpose}
      </p>

      <div className="flex flex-wrap items-center gap-3 text-small text-text-tertiary">
        {grant.lastUsedAt ? (
          <span>
            {t("Last used {{when}}", {
              when: formatRelativeTime(grant.lastUsedAt, i18n.language),
            })}
          </span>
        ) : null}
        {asOwner ? (
          <span>
            {t("Caused spend: {{tokens}} tokens", {
              tokens: grant.causedSpendTokens.toLocaleString(i18n.language),
            })}
          </span>
        ) : null}
        {cooldownUntil && cooldownUntil > Date.now() ? (
          <span>
            {t("You can request again {{when}}", {
              when: formatRelativeTime(new Date(cooldownUntil).toISOString(), i18n.language),
            })}
          </span>
        ) : null}
      </div>

      {grant.status === "pending" && isOwner ? <GrantReviewRow grant={grant} /> : null}

      {grant.status === "active" ? (
        <div className="flex flex-wrap gap-2">
          {asOwner ? (
            <Button size="sm" variant="ghost" onClick={() => setExpanded((open) => !open)}>
              {expanded ? t("Hide activity") : t("Activity")}
            </Button>
          ) : null}
          {canRevoke ? (
            <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              {asOwner ? t("Revoke") : t("Renounce")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {expanded ? <GrantActivity grantId={grant.id} /> : null}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t("Revoke this grant?")}
        description={t(
          "In-flight work under it is cancelled immediately, and any threads it created are dropped.",
        )}
        confirmLabel={t("Revoke")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={revoke.isPending}
        onConfirm={() =>
          revoke.mutate(grant.id, {
            onSuccess: () => {
              setConfirming(false);
              toast.success(t("Grant revoked"));
            },
            onError: (error) =>
              toast.error(error instanceof Error ? error.message : t("Could not revoke the grant")),
          })
        }
      />
    </li>
  );
}

/**
 * Temporary, scoped access to somebody else's editor window.
 *
 * Two rules on this surface are load-bearing and neither is obvious from the endpoint list:
 * **only the owner** approves, declines or escalates (an admin is not a path into a
 * colleague's machine — G6), and **`list` and `revoke` keep working with the workspace switch
 * OFF**, because a person must always be able to see and kill access they have given,
 * whatever an admin has since turned off.
 */
export function RemoteAccessPanel() {
  const { t } = useTranslation();
  const { data: viewer } = useViewer();
  const { data: workspace } = useCurrentWorkspace();
  const { data: grants, isLoading } = useIdeGrants();
  const updatePreferences = useUpdateWorkspacePreferences();

  const canAdminister = workspace?.abilities[ABILITY.workspaceUpdate] === true;
  const grantsEnabled = workspace?.preferences.ideGrantsEnabled === true;

  return (
    <div className="flex flex-col gap-4">
      <Text variant="small">
        {t(
          "Ask a teammate for temporary, scoped access to their connected VS Code window, or manage the access you've given out. Every grant is narrowest-by-default, expires on its own, and every use is recorded.",
        )}
      </Text>

      {canAdminister ? (
        <section className="flex flex-col gap-1">
          <h3 className="text-body font-medium text-text">{t("Workspace switch")}</h3>
          <Switch
            checked={grantsEnabled}
            disabled={updatePreferences.isPending}
            label={t("Allow cross-person IDE grants")}
            description={t(
              "Admins can disable cross-person grants workspace-wide. Admins cannot create or escalate grants — access to a person's machine is that person's to give.",
            )}
            onCheckedChange={(next) =>
              updatePreferences.mutate(
                { ideGrantsEnabled: next },
                {
                  onSuccess: () => toast.success(t("Settings saved")),
                  onError: () => toast.error(t("Could not save the setting")),
                },
              )
            }
          />
        </section>
      ) : null}

      {isLoading ? <Spinner label={t("Loading")} /> : null}

      <section className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Grants I've given")}</h3>
        {(grants?.asOwner ?? []).length === 0 ? (
          <Empty
            icon={LockKey}
            message={t("Nobody has asked for access to your editor windows.")}
          />
        ) : (
          <ul className="flex flex-col rounded-md border border-divider">
            {(grants?.asOwner ?? []).map((grant) => (
              <GrantRow
                key={grant.id}
                grant={grant}
                viewerId={viewer?.id}
                canAdminister={canAdminister === true}
                asOwner
              />
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("My requests")}</h3>
        {(grants?.asGrantee ?? []).length === 0 ? (
          <Empty message={t("You have not asked anyone for access to their editor.")} />
        ) : (
          <ul className="flex flex-col rounded-md border border-divider">
            {(grants?.asGrantee ?? []).map((grant) => (
              <GrantRow
                key={grant.id}
                grant={grant}
                viewerId={viewer?.id}
                canAdminister={canAdminister === true}
                asOwner={false}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
